// V4 — Mobile-first Schedule view.
//
// Nav model:
//   view = "list"     → the main list of matches (upcoming + past sections)
//   view = "form"     → full-screen create/edit form (no longer inline)
//
// Card actions:
//   • Edit  → transitions to the form screen (never opens inline above)
//   • RSVP  → expands inline on the same card so the user stays put;
//             highlights their current pick if they've already RSVP'd
//   • Squad → if the squad is published, opens the flyer in a viewer
//             modal (portalled to <body>); otherwise a toast letting
//             the user know they'll be notified once it's announced.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, Pencil, Trash2, Filter, CheckCircle2, ClipboardList,
  ScrollText, CalendarDays, Clock, MapPin, Tag,
  ArrowLeft, Save, X, Check, HelpCircle, XCircle, Bell,
  Users, ChevronDown, ChevronUp
} from "lucide-react";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import MatchResultEntry from "../components/MatchResultEntry";
import { PosterPreview } from "../components/flyers/FlyerLegacy";
import {
  rulesForMatchAndTeam, DEFAULT_CAPTAIN,
  defaultReportingTime, parseSquadNotes
} from "../components/flyers/flyerHelpers";
import { fmtMatchTime } from "../utils/time";
import { api } from "../api";
import { canDo } from "../data";

const SPORT_FILTERS = ["All", "Cricket", "Volleyball"];
const TYPES = ["League Match", "Friendly", "Tournament", "Practice"];
const STATUSES = ["Scheduled", "Completed", "Cancelled", "Postponed"];
const ELEVATED_ROLES = ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"];
const RSVP_OPTIONS = [
  { value: "Available",     label: "Yes",   color: "var(--green)",  Icon: CheckCircle2 },
  { value: "Tentative",     label: "Maybe", color: "var(--orange)", Icon: HelpCircle   },
  { value: "Not Available", label: "No",    color: "var(--red)",    Icon: XCircle      }
];
const EMPTY_FORM = {
  teamId: "", sport: "Cricket", opponent: "", date: "", time: "",
  venue: "", type: "League Match", status: "Scheduled", notes: ""
};

function dayBadge(iso) {
  if (!iso) return "TBD";
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(`${iso}T00:00:00`);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0)  return "TODAY";
  if (diff === 1)  return "TOMORROW";
  if (diff === -1) return "YESTERDAY";
  return d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
}
function dateLong(iso) {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function ScheduleMobile({ state, reload, user, navigate }) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [sportFilter, setSportFilter] = useState("All");
  const [resultFor, setResultFor] = useState(null);

  // ── Page-level nav (form is its own screen; no more top-of-page modal)
  const [view, setView]           = useState("list");     // "list" | "form"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);

  // ── Per-card UI state
  const [rsvpOpen, setRsvpOpen]     = useState(null);     // matchId currently expanded
  const [viewingSquad, setViewingSquad] = useState(null); // { match, squad, ... } or null

  const canCreate       = canDo(user, "schedule", "create");
  const canUpdate       = canDo(user, "schedule", "update");
  const canDelete       = canDo(user, "schedule", "delete");
  const canRecordResult = canDo(user, "matchPerformance", "update");
  const today = new Date().toISOString().slice(0, 10);

  // Next 7 days cutoff — everything up to (but not including) this date
  // shows in the "Next 7 Days" hero at the top, ACROSS ALL TEAMS.
  const in7DaysIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  }, []);

  // Teams the user can see, alphabetical — used for the tab row.
  const teamTabs = useMemo(() => {
    return [...state.teams]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [state.teams]);

  // Active team tab — defaults to the first team, or the global active
  // team if it matches one of the tabs.
  const [activeTab, setActiveTab] = useState(() => {
    return (!isAllTeams && activeTeamId) ? activeTeamId : (teamTabs[0]?.id || null);
  });
  useEffect(() => {
    // If the global active team switches to a specific team, sync the tab.
    if (!isAllTeams && activeTeamId) setActiveTab(activeTeamId);
  }, [isAllTeams, activeTeamId]);

  // ── The two views ────────────────────────────────────────────────────
  // Next 7 Days — all teams pooled, sport filter still applies.
  const next7Days = useMemo(() => {
    let pool = state.matches
      .filter(m => m.date >= today && m.date < in7DaysIso);
    if (sportFilter !== "All") pool = pool.filter(m => m.sport === sportFilter);
    return [...pool].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [state.matches, today, in7DaysIso, sportFilter]);

  // Team-tab view — everything else for the picked team.
  const teamView = useMemo(() => {
    if (!activeTab) return { upcoming: [], past: [] };
    let pool = state.matches.filter(m => m.teamId === activeTab);
    if (sportFilter !== "All") pool = pool.filter(m => m.sport === sportFilter);
    // Exclude matches already shown in the Next 7 Days block so they
    // don't appear twice.
    pool = pool.filter(m => m.date < today || m.date >= in7DaysIso);
    const sorted = [...pool].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    return {
      upcoming: sorted.filter(m => m.date >= in7DaysIso),
      past:     sorted.filter(m => m.date < today).reverse()
    };
  }, [state.matches, activeTab, today, in7DaysIso, sportFilter]);

  const totalCount = next7Days.length + teamView.upcoming.length + teamView.past.length;

  // Roles elevated globally (admin / president / VP / cricket-head / volleyball-head).
  const isElevated = user?.roles?.some(r => ELEVATED_ROLES.includes(r));

  // ── Form nav ─────────────────────────────────────────────────────────
  function openCreate() {
    const seedTeam = activeTeam || state.teams[0];
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      teamId: seedTeam?.id || "",
      sport: seedTeam?.sport || "Cricket"
    });
    setView("form");
    window.scrollTo({ top: 0 });
  }
  function openEdit(m) {
    setEditingId(m.id);
    setForm({
      teamId: m.teamId, sport: m.sport, opponent: m.opponent,
      date: m.date, time: m.time, venue: m.venue,
      type: m.type || "League Match", status: m.status || "Scheduled",
      notes: m.notes || ""
    });
    setView("form");
    window.scrollTo({ top: 0 });
  }
  function cancelForm() {
    setView("list");
    setEditingId(null);
  }
  async function saveForm() {
    if (!form.teamId || !form.opponent.trim() || !form.date || !form.time || !form.venue.trim()) {
      toast.error("Team, opponent, date, time and venue are required.");
      return;
    }
    try {
      const payload = {
        teamId: Number(form.teamId), sport: form.sport,
        opponent: form.opponent.trim(), date: form.date, time: form.time,
        venue: form.venue.trim(), type: form.type, status: form.status,
        notes: form.notes.trim() || null
      };
      if (editingId) await api.updateMatch(editingId, payload);
      else           await api.createMatch(payload);
      await reload();
      toast.success(editingId ? "Match updated." : "Match added.");
      cancelForm();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    }
  }

  async function remove(m) {
    const teamName = state.teams.find(t => t.id === m.teamId)?.name || "Pitt Gabruz";
    if (!confirm(`Delete this match (${teamName} vs ${m.opponent} on ${m.date})?`)) return;
    try {
      await api.deleteMatch(m.id);
      await reload();
      toast.success("Match deleted.");
    } catch (err) { toast.error("Delete failed: " + err.message); }
  }

  // ── RSVP inline. userId defaults to current user; admin/captain
  //    can pass any user's ID to RSVP on their behalf. ──────────────
  async function setRsvp(matchId, userId, status) {
    try {
      await api.setAvailability(matchId, userId, { status, comments: "" });
      await reload();
      const isSelf = userId === user.id;
      toast.success(isSelf ? `Marked as ${status}.` : `RSVP saved.`);
    } catch (err) { toast.error(err.message || "Couldn't save RSVP."); }
  }
  function myRsvpFor(matchId, uid = user.id) {
    return state.availability.find(a => a.matchId === matchId && a.userId === uid);
  }

  // ── Squad button — published? open flyer. Not published? notify user.
  function openSquad(m) {
    const squad = state.squads.find(s => s.matchId === m.id);
    if (!squad || !squad.isPublished) {
      toast.info("Playing Squad is not published yet. You'll be notified once it's announced.", { duration: 6000 });
      return;
    }
    const team   = state.teams.find(t => t.id === m.teamId) || null;
    const parsed = parseSquadNotes(squad.notes);
    const lookup = ids => (ids || []).map(id => state.members.find(u => u.id === id)).filter(Boolean);
    const rules  = rulesForMatchAndTeam(m, team);
    setViewingSquad({
      match: m, squad, parsed, team, rules,
      startingPlayers:      lookup(squad.players),
      benchPlayers:         lookup(squad.substitutes),
      teamBStartingPlayers: lookup(squad.teamBPlayers),
      teamBBenchPlayers:    lookup(squad.teamBSubstitutes)
    });
  }

  // ================================================================
  // FORM VIEW — full screen (no inline modal above)
  // ================================================================
  if (view === "form") {
    return (
      <div className="m-sched">
        <header className="m-sched-formHead">
          <button type="button" className="m-sched-formBack" onClick={cancelForm} aria-label="Back">
            <ArrowLeft size={18}/>
          </button>
          <h1>{editingId ? "Edit Match" : "New Match"}</h1>
        </header>

        <div className="m-sched-formBody">
          <label>
            Team
            <select
              value={form.teamId}
              onChange={e => {
                const t = state.teams.find(x => x.id === +e.target.value);
                setForm({ ...form, teamId: e.target.value, sport: t?.sport || form.sport });
              }}
            >
              <option value="">— select —</option>
              {state.teams.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.sport})</option>
              ))}
            </select>
          </label>

          <label>
            Opponent
            <input
              value={form.opponent}
              onChange={e => setForm({ ...form, opponent: e.target.value })}
              placeholder="Steel City Strikers"
            />
          </label>

          <label>
            Date
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}/>
          </label>

          <label>
            Time
            <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })}/>
          </label>

          <label>
            Venue
            <input
              value={form.venue}
              onChange={e => setForm({ ...form, venue: e.target.value })}
              placeholder="Linbrook Park"
            />
          </label>

          <label>
            Match Type
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              {TYPES.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>

          <label>
            Status
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>

          <label>
            Notes (optional)
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Anything the team should know…"
            />
          </label>
        </div>

        <div className="m-sched-formActions">
          <button type="button" className="dark" onClick={cancelForm}>
            <X size={14}/> Cancel
          </button>
          <button type="button" className="btn" onClick={saveForm}>
            <Save size={14}/> {editingId ? "Update Match" : "Save Match"}
          </button>
        </div>
      </div>
    );
  }

  // ================================================================
  // LIST VIEW — main schedule screen
  // ================================================================
  return (
    <div className="m-sched">
      <header className="m-sched-head">
        <div>
          <small>{isAllTeams ? "All Teams" : (activeTeam?.name || "Pitt Gabruz")}</small>
          <h1>Schedule</h1>
        </div>
        {canCreate && (
          <button type="button" className="btn m-sched-addBtn" onClick={openCreate}>
            <Plus size={16}/> Add
          </button>
        )}
      </header>

      {/* Sport filter chips (Cricket / Volleyball / All) */}
      <div className="m-sched-filterRow">
        <Filter size={14} style={{color:"var(--gold)"}}/>
        {SPORT_FILTERS.map(s => (
          <button
            key={s}
            type="button"
            className={sportFilter === s ? "m-sched-filterChip m-sched-filterChip--active" : "m-sched-filterChip"}
            onClick={() => setSportFilter(s)}
          >{s}</button>
        ))}
        <small style={{marginLeft:"auto"}}>{totalCount}</small>
      </div>

      {/* ── NEXT 7 DAYS ── pooled across all teams ────────────────── */}
      {next7Days.length > 0 && (
        <section className="m-sched-section m-sched-section--week">
          <h3 className="m-sched-h3 m-sched-h3--hero">
            Next 7 Days · <em>{next7Days.length}</em>
          </h3>
          <div className="m-sched-list">
            {next7Days.map(m => (
              <MatchCard key={m.id} m={m} state={state}
                user={user}
                isElevated={isElevated}
                rsvpOpen={rsvpOpen === m.id}
                onToggleRsvp={() => setRsvpOpen(rsvpOpen === m.id ? null : m.id)}
                onSetRsvp={(userId, status) => setRsvp(m.id, userId, status)}
                myRsvpFor={myRsvpFor}
                onSquad={() => openSquad(m)}
                onEdit={canUpdate ? () => openEdit(m) : null}
                onDelete={canDelete ? () => remove(m) : null}
                onResult={canRecordResult && m.sport === "Cricket" && m.date <= today ? () => setResultFor(m) : null}
                isPast={false}
                showTeamBadge/>
            ))}
          </div>
        </section>
      )}

      {/* ── TEAM TABS ─────────────────────────────────────────────── */}
      {teamTabs.length > 0 && (
        <div className="m-sched-tabs" role="tablist">
          {teamTabs.map(t => {
            const isActive = t.id === activeTab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`m-sched-tab${isActive ? " m-sched-tab--active" : ""}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {/* ── TEAM VIEW: upcoming (beyond 7 days) + past ────────────── */}
      {teamView.upcoming.length > 0 && (
        <section className="m-sched-section">
          <h3 className="m-sched-h3">
            Upcoming · <em>{teamView.upcoming.length}</em>
            <small className="m-sched-h3-sub"> beyond next 7 days</small>
          </h3>
          <div className="m-sched-list">
            {teamView.upcoming.map(m => (
              <MatchCard key={m.id} m={m} state={state}
                user={user}
                isElevated={isElevated}
                rsvpOpen={rsvpOpen === m.id}
                onToggleRsvp={() => setRsvpOpen(rsvpOpen === m.id ? null : m.id)}
                onSetRsvp={(userId, status) => setRsvp(m.id, userId, status)}
                myRsvpFor={myRsvpFor}
                onSquad={() => openSquad(m)}
                onEdit={canUpdate ? () => openEdit(m) : null}
                onDelete={canDelete ? () => remove(m) : null}
                onResult={canRecordResult && m.sport === "Cricket" && m.date <= today ? () => setResultFor(m) : null}
                isPast={false}/>
            ))}
          </div>
        </section>
      )}

      {teamView.past.length > 0 && (
        <section className="m-sched-section">
          <h3 className="m-sched-h3 m-sched-h3--muted">
            Past · <em>{teamView.past.length}</em>
          </h3>
          <div className="m-sched-list">
            {teamView.past.map(m => (
              <MatchCard key={m.id} m={m} state={state}
                user={user}
                isElevated={isElevated}
                myRsvpFor={myRsvpFor}
                onSquad={() => openSquad(m)}
                onEdit={canUpdate ? () => openEdit(m) : null}
                onDelete={canDelete ? () => remove(m) : null}
                onResult={canRecordResult && m.sport === "Cricket" ? () => setResultFor(m) : null}
                isPast={true}/>
            ))}
          </div>
        </section>
      )}

      {totalCount === 0 && (
        <div className="m-sched-empty">
          <CalendarDays size={32}/>
          <p>No matches{sportFilter !== "All" ? ` for ${sportFilter}` : ""}.</p>
          {canCreate && (
            <button type="button" className="btn" onClick={openCreate}>
              <Plus size={14}/> Add a match
            </button>
          )}
        </div>
      )}

      {resultFor && (
        <MatchResultEntry
          match={resultFor}
          team={state.teams.find(t => t.id === resultFor.teamId)}
          squad={state.squads.find(s => s.matchId === resultFor.id)}
          members={state.members}
          user={user}
          onClose={() => setResultFor(null)}
          onSaved={() => reload && reload()}
        />
      )}

      {viewingSquad && (
        <SquadViewer
          state={state}
          viewing={viewingSquad}
          onClose={() => setViewingSquad(null)}
        />
      )}
    </div>
  );
}

// ── Squad viewer modal — portalled to <body> so the V4 nav can't cover it.
function SquadViewer({ state, viewing, onClose }) {
  const flyerTemplate = useMemo(() => {
    const templates = state.flyerTemplates || [];
    return templates.find(t => t.teamId === viewing.team?.id) || null;
  }, [state.flyerTemplates, viewing.team]);

  return createPortal((
    <div className="modal" onClick={onClose}>
      <div className="modalInner squad-viewer-inner" onClick={e => e.stopPropagation()}>
        <button className="close" onClick={onClose} title="Close"><X size={20}/></button>
        <div className="snapshotZone">
          <PosterPreview
            match={viewing.match}
            opponent={viewing.parsed.opponent || viewing.match.opponent}
            captainMsg={viewing.parsed.captain || DEFAULT_CAPTAIN}
            reportingTime={viewing.parsed.reportingTime || defaultReportingTime(viewing.match.time)}
            venue={viewing.match.venue || viewing.rules?.defaultVenue || ""}
            startingPlayers={viewing.startingPlayers.map(p => ({ id: p.id, name: p.name }))}
            benchPlayers={viewing.benchPlayers.map(p => ({ id: p.id, name: p.name }))}
            teamBStartingPlayers={(viewing.teamBStartingPlayers || []).map(p => ({ id: p.id, name: p.name }))}
            teamBBenchPlayers={(viewing.teamBBenchPlayers || []).map(p => ({ id: p.id, name: p.name }))}
            snacksVolunteer={viewing.parsed.snacks}
            alcoholVolunteer={viewing.parsed.alcohol}
            captainPickId={viewing.parsed.captainPickId}
            wicketKeeperId={viewing.parsed.wicketKeeperId}
            rules={viewing.rules}
            team={viewing.team}
            flyerTemplate={flyerTemplate}
          />
        </div>
      </div>
    </div>
  ), document.body);
}

// ================================================================
// MATCH CARD
// ================================================================
function MatchCard({
  m, state, user, isPast, isElevated, showTeamBadge,
  rsvpOpen, onToggleRsvp, onSetRsvp, myRsvpFor,
  onSquad, onEdit, onDelete, onResult
}) {
  const team = state.teams.find(t => t.id === m.teamId);
  const homeShort = (team?.name || "Pitt Gabruz")
    .replace(/(Cricket|Volleyball|Team|Club)/gi, "")
    .trim() || "Pitt Gabruz";
  const counts = useMemo(() => {
    const rows = state.availability.filter(a => a.matchId === m.id);
    return {
      a: rows.filter(r => r.status === "Available").length,
      t: rows.filter(r => r.status === "Tentative").length,
      n: rows.filter(r => r.status === "Not Available").length
    };
  }, [m.id, state.availability]);

  const onRoster = (team?.players || []).includes(user?.id);
  // Elevated globally OR the team's captain — can RSVP for others.
  const canManageOthers = isElevated || team?.captainId === user?.id;
  const myRsvp = myRsvpFor ? myRsvpFor(m.id) : null;

  return (
    <article className={`m-sched-card${isPast ? " m-sched-card--past" : ""}`}>
      <div className="m-sched-card-dayStrip">
        <span className="m-sched-card-day">{dayBadge(m.date)}</span>
        <span className="m-sched-card-date">{dateLong(m.date)}</span>
        <span className="m-sched-card-time"><Clock size={11}/> {m.time ? `${fmtMatchTime(m.time)} ET` : "TBD"}</span>
        <span className="m-sched-card-sport">{m.sport}</span>
        {showTeamBadge && team?.name && (
          <span className="m-sched-card-teamBadge">{team.name}</span>
        )}
      </div>

      <div className="m-sched-card-vs">
        <div className="m-sched-card-team">
          <small>HOME</small>
          <b>{homeShort.toUpperCase()}</b>
        </div>
        <span className="m-sched-card-vsLabel">VS</span>
        <div className="m-sched-card-team">
          <small>AWAY</small>
          <b>{m.opponent.toUpperCase()}</b>
        </div>
      </div>

      <div className="m-sched-card-meta">
        {m.venue && <span className="m-sched-card-chip"><MapPin size={11}/> {m.venue}</span>}
        <span className="m-sched-card-chip"><Tag size={11}/> {m.type || "League Match"}</span>
        <span className={`m-sched-card-status m-sched-card-status--${(m.status || "scheduled").toLowerCase()}`}>
          {m.status || "Scheduled"}
        </span>
      </div>

      {!isPast && (counts.a + counts.t + counts.n > 0) && (
        <div className="m-sched-card-rsvp">
          <span style={{color:"var(--green)"}}>{counts.a} Yes</span>
          <span style={{color:"var(--orange)"}}>{counts.t} Maybe</span>
          <span style={{color:"var(--red)"}}>{counts.n} No</span>
        </div>
      )}

      {/* Inline RSVP panel — self only, or full team roster for admins/captains */}
      {rsvpOpen && !isPast && (onRoster || canManageOthers) && (
        <RsvpInline
          match={m}
          team={team}
          state={state}
          user={user}
          onRoster={onRoster}
          canManageOthers={canManageOthers}
          myRsvp={myRsvp}
          onSet={onSetRsvp}
          myRsvpFor={myRsvpFor}
        />
      )}

      <div className="m-sched-card-actions">
        {!isPast && onToggleRsvp && (onRoster || canManageOthers) && (
          <button
            type="button"
            className={`dark m-sched-card-rsvpBtn${myRsvp ? " m-sched-card-rsvpBtn--set" : ""}`}
            onClick={onToggleRsvp}
            style={myRsvp ? { borderColor: statusColor(myRsvp.status), color: statusColor(myRsvp.status) } : null}
          >
            <CheckCircle2 size={13}/>
            {myRsvp ? `RSVP: ${labelFor(myRsvp.status)}` : (canManageOthers && !onRoster ? "Manage RSVPs" : "RSVP")}
          </button>
        )}
        <button type="button" className="dark" onClick={onSquad}>
          <ClipboardList size={13}/> Squad
        </button>
        {onResult && (
          <button type="button" className="dark m-sched-card-resultBtn" onClick={onResult}>
            <ScrollText size={13}/> Result
          </button>
        )}
        {onEdit && (
          <button type="button" className="dark m-sched-editBtn" onClick={onEdit} title="Edit schedule details">
            <Pencil size={13}/> Edit
          </button>
        )}
        {onDelete && (
          <button type="button" className="dark" onClick={onDelete} title="Delete"
                  style={{color:"var(--red)", borderColor:"rgba(255,107,107,0.4)"}}>
            <Trash2 size={13}/>
          </button>
        )}
      </div>
    </article>
  );
}

function statusColor(s) {
  const o = RSVP_OPTIONS.find(x => x.value === s);
  return o?.color || "var(--muted)";
}
function labelFor(s) {
  const o = RSVP_OPTIONS.find(x => x.value === s);
  return o?.label || "—";
}

// ── Inline RSVP block. Self section for the user, then (for admins /
// captains) a collapsible team roster where they can RSVP anyone.
function RsvpInline({
  match, team, state, user,
  onRoster, canManageOthers, myRsvp, onSet, myRsvpFor
}) {
  const [teamOpen, setTeamOpen] = useState(false);
  const roster = useMemo(() => {
    if (!team) return [];
    return (team.players || [])
      .map(id => state.members.find(u => u.id === id))
      .filter(Boolean)
      .filter(p => p.id !== user.id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [team, state.members, user.id]);

  return (
    <div className="m-sched-card-rsvpInline">
      {/* Section 1: YOUR RSVP (only if on the team roster) */}
      {onRoster && (
        <>
          {myRsvp ? (
            <p className="m-sched-card-rsvpInline-note">
              <Bell size={12} style={{color: statusColor(myRsvp.status)}}/>
              You've already RSVP'd as <b style={{color: statusColor(myRsvp.status)}}>{labelFor(myRsvp.status)}</b>. You can change it below.
            </p>
          ) : (
            <p className="m-sched-card-rsvpInline-note">Are you playing this match?</p>
          )}
          <div className="m-sched-card-rsvpInline-btns">
            {RSVP_OPTIONS.map(o => {
              const active = myRsvp?.status === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  className={`m-sched-card-rsvpInline-btn${active ? " m-sched-card-rsvpInline-btn--active" : ""}`}
                  onClick={() => onSet(user.id, o.value)}
                  style={active ? { background: o.color, borderColor: o.color, color: "#0c1424" } : { color: o.color, borderColor: o.color }}
                >
                  <o.Icon size={13}/> {o.label}
                  {active && <Check size={13} style={{marginLeft:4}}/>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Section 2: TEAM ROSTER (admin/captain only) */}
      {canManageOthers && roster.length > 0 && (
        <div className="m-sched-teamRsvp">
          <button
            type="button"
            className="m-sched-teamRsvp-toggle"
            onClick={() => setTeamOpen(o => !o)}
            aria-expanded={teamOpen}
          >
            <Users size={13}/>
            <span>{teamOpen ? "Hide team" : "Manage team RSVPs"}</span>
            <em>{roster.length} players</em>
            {teamOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          </button>
          {teamOpen && (
            <ul className="m-sched-teamRsvp-list">
              {roster.map(p => {
                const rsvp = myRsvpFor ? myRsvpFor(match.id, p.id) : null;
                return (
                  <li key={p.id} className="m-sched-teamRsvp-row">
                    <span className="m-sched-teamRsvp-name">
                      {p.name}
                      {rsvp && (
                        <em style={{color: statusColor(rsvp.status)}}>
                          {labelFor(rsvp.status)}
                        </em>
                      )}
                    </span>
                    <div className="m-sched-teamRsvp-btns">
                      {RSVP_OPTIONS.map(o => {
                        const active = rsvp?.status === o.value;
                        return (
                          <button
                            key={o.value}
                            type="button"
                            className={`m-sched-teamRsvp-btn${active ? " m-sched-teamRsvp-btn--active" : ""}`}
                            onClick={() => onSet(p.id, o.value)}
                            title={o.label}
                            style={active ? { background: o.color, borderColor: o.color, color: "#0c1424" } : { color: o.color, borderColor: o.color }}
                          >
                            {o.label.charAt(0)}
                          </button>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
